import React, { useEffect, useState, useContext, useMemo } from 'react';
import { AuthContext } from "../AuthContext";
import { Chart } from 'react-google-charts';
import { motion } from "framer-motion";
import {
  FileText,
  ShieldCheck,
  ShieldAlert,
  Lock,
  Truck,
  Car,
  Leaf,
  BarChart2,
  Download,
  Search,
  CheckCircle,
  Clock,
  User as UserIcon,
  RefreshCw,
  X,
  LogOut
} from "lucide-react";

import KYCSection from "../components/KYCSection";
import MilestonesAndShare from "../components/MilestonesAndShare";
import natureImg from '../resource/natureImg.png';
import { handleDownload } from './CarbonFootprint';
import axios from "axios";
import API_BASE_URL from "../api";

export const UserDashBoard = () => {
  const { user, refreshUser, logout } = useContext(AuthContext);
  const userId = user?.userId || user?._id;
  const userName = user?.userName || user?.baseUsername || "User";
  const [showKYCModal, setShowKYCModal] = useState(false);

  const openKYCModal = () => setShowKYCModal(true);
  const closeKYCModal = () => setShowKYCModal(false);

  const isKycVerified = Boolean(
    user?.kycVerified ||
    user?.kycStatus === 'VERIFIED' ||
    user?.kyc?.aadhar?.verified ||
    user?.kyc?.pan?.verified ||
    user?.kyc?.drivingLicence?.verified ||
    localStorage.getItem("kycVerified") === "true"
  );

  const [dieselVehiclesData, setDieselVehiclesData] = useState([]);
  const [dieselVehiclesData1, setDieselVehiclesData1] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("fuel");
  const [selectedFuel, setSelectedFuel] = useState("DIESEL");
  const [selectedDateRange, setSelectedDateRange] = useState("Weekly");
  const [routeWiseEmissionData, setRouteWiseEmissionData] = useState([]);

  const [timeFilter, setTimeFilter] = useState("All");
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const data = useMemo(() => {
    if (routeWiseEmissionData.length === 0) {
      return [["Route", "Emission"], ["No Data", 1]];
    }
    const sorted = [...routeWiseEmissionData].sort((a, b) => b.totalEmission - a.totalEmission);
    const top10 = sorted.slice(0, 10);
    const othersSum = sorted.slice(10).reduce((sum, route) => sum + route.totalEmission, 0);

    const chartData = [
      ["Route", "Emission"],
      ...top10.map((route) => [route.route, route.totalEmission]),
    ];

    if (othersSum > 0) {
      chartData.push(["Others", othersSum]);
    }

    return chartData;
  }, [routeWiseEmissionData]);

  useEffect(() => {
    if (refreshUser) {
      refreshUser();
    }
  }, [refreshUser]);

  const getDateRange = (range) => {
    const today = new Date();
    const endDate = today.toISOString().split("T")[0];
    let startDate;

    switch (range) {
      case "Daily":
        startDate = endDate;
        break;
      case "Weekly":
        startDate = new Date(today.setDate(today.getDate() - 7)).toISOString().split("T")[0];
        break;
      case "Monthly":
        startDate = new Date(today.setMonth(today.getMonth() - 1)).toISOString().split("T")[0];
        break;
      case "Yearly":
        startDate = new Date(today.setFullYear(today.getFullYear() - 1)).toISOString().split("T")[0];
        break;
      default:
        startDate = endDate;
    }

    return { startDate, endDate };
  };

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const fetchCarbonFootprintData = async () => {
      try {
        setLoading(true);
        const [response1, response2, routeResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/vehicle/carbonfootprint/dieselvehicles/${userId}`),
          fetch(`${API_BASE_URL}/api/vehicle/carbonfootprint/dieselvehicles1/${userId}`),
          fetch(`${API_BASE_URL}/api/vehicle/routewiseEmission/${userId}`)
        ]);

        if (!response1.ok) throw new Error("Failed to fetch vehicles carbon footprint data");
        if (!response2.ok) throw new Error("Failed to fetch vehicles detailed data");
        if (!routeResponse.ok) throw new Error("Failed to fetch route-wise emission data");

        const data1 = await response1.json();
        const data2 = await response2.json();
        const routeData = await routeResponse.json();

        setDieselVehiclesData(data1?.carbonFootprintData || []);
        setFilteredData(data1?.carbonFootprintData || []);
        setDieselVehiclesData1(data2?.carbonFootprintData || []);
        setRouteWiseEmissionData(routeData?.routewiseEmissionData || []);
      } catch (err) {
        console.error("Fetch Error:", err);
        setError(err.message || "Failed to fetch dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchCarbonFootprintData();
  }, [userId]);

  useEffect(() => {
    if (!dieselVehiclesData.length) return;

    let dateLabels = new Map();
    const now = new Date();

    dieselVehiclesData.forEach((item) => {
      const itemDate = new Date(item.date);
      let label = "";

      if (timeFilter === "All") {
        label = itemDate.toISOString().split("T")[0];
      } else if (timeFilter === "Week") {
        const weekAgo = new Date();
        weekAgo.setDate(now.getDate() - 7);
        if (itemDate >= weekAgo) {
          label = itemDate.toLocaleDateString("en-US", { weekday: "short" });
        }
      } else if (timeFilter === "Month") {
        label = `Week ${Math.ceil(itemDate.getDate() / 7)}`;
      } else if (timeFilter === "Year") {
        label = itemDate.toLocaleDateString("en-US", { month: "long" });
      }

      if (label) {
        dateLabels.set(label, (dateLabels.get(label) || 0) + parseFloat(item.carbonFootprint || 0));
      }
    });

    const filtered = Array.from(dateLabels.entries()).map(([date, value]) => ({
      date,
      carbonFootprint: value,
    }));

    setFilteredData(filtered);
  }, [timeFilter, dieselVehiclesData]);

  const downloadCSVReport = async () => {
    try {
      if (!selectedFuel && !selectedDateRange) {
        alert("Please select fuel type or date range");
        return;
      }

      let url = `${API_BASE_URL}/api/vehicle/carbonfootprintbyfueltype?`;

      if (selectedFuel) url += `fuelType=${selectedFuel}&`;

      if (selectedDateRange) {
        const { startDate, endDate } = getDateRange(selectedDateRange);
        url += `startDate=${startDate}&endDate=${endDate}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Failed to fetch report data");
      }

      const reportData = await response.json();

      if (!reportData.carbonFootprintData || !reportData.carbonFootprintData.length) {
        alert("No carbon footprint data available for selected filter.");
        return;
      }

      const csvContent = [
        ["Date", "Vehicle Number", "Fuel Type", "Carbon Emission (kg CO2)", "Last Updated", "PDF Link"],
        ...reportData.carbonFootprintData.map((item) => [
          item.date,
          item.vehicleNumber,
          item.fuelType,
          item.carbonFootprint,
          new Date(item.updatedAt).toLocaleDateString(),
          item.pdfUrl ? item.pdfUrl : "N/A"
        ]),
      ]
        .map((row) => row.join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Carbon_Footprint_Report_${selectedFuel || selectedDateRange}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error("Error downloading report:", error);
      alert("Failed to download CSV report.");
    }
  };

  const topEmittingVehicle =
    dieselVehiclesData.length > 0
      ? dieselVehiclesData.reduce(
        (max, item) =>
          parseFloat(item.carbonFootprint || 0) > parseFloat(max.carbonFootprint || 0) ? item : max,
        dieselVehiclesData[0]
      )
      : null;

  const totalVehicles = dieselVehiclesData1.length;
  const totalEmission = dieselVehiclesData.reduce((total, item) => total + parseFloat(item.carbonFootprint || 0), 0);

  const filteredFleetList = useMemo(() => {
    if (!searchQuery.trim()) return dieselVehiclesData1;
    return dieselVehiclesData1.filter((item) =>
      item.vehicleNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.fuelType?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [dieselVehiclesData1, searchQuery]);

  const chartOptions = {
    title: "",
    backgroundColor: "#F9FAFB",
    pieHole: 0.35,
    slices: {
      0: { color: "#10B981" },
      1: { color: "#3B82F6" },
      2: { color: "#F59E0B" },
      3: { color: "#8B5CF6" },
      4: { color: "#EC4899" },
    },
    legend: { position: "right", textStyle: { color: "#374151", fontSize: 13 } },
    chartArea: { width: "90%", height: "85%" },
  };

  const handleAadharSubmit = async (formData) => {
    try {
      const activeUserId = user?._id || user?.userId || userId;
      const response = await axios.post(`${API_BASE_URL}/api/kyc/verify/aadhar`, {
        ...formData,
        userId: activeUserId,
      });

      alert("Aadhar Verified Successfully!");
      if (refreshUser) await refreshUser();
      closeKYCModal();
    } catch (error) {
      console.error("❌ Aadhar Verification Failed:", error.response?.data || error.message);
      alert("Aadhar verification failed: " + (error.response?.data?.message || error.response?.data?.error || error.message));
    }
  };

  const handlePanSubmit = async (data) => {
    const activeUserId = user?._id || user?.userId || userId;
    const requestBody = {
      panno: data.panno,
      PANFullName: data.PANFullName,
      userId: activeUserId,
      code: "mock",
      code_verifier: "mock",
    };

    if (!requestBody.userId) {
      alert("❌ User ID is missing! Cannot proceed.");
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/kyc/verify/pan`, requestBody);
      alert("PAN Verified Successfully!");
      if (refreshUser) await refreshUser();
      closeKYCModal();
    } catch (err) {
      console.error("❌ PAN Verification Failed:", err?.response?.data?.message || err.message);
      alert("PAN verification failed: " + (err?.response?.data?.message || err.message));
    }
  };

  const handleDlSubmit = async (data) => {
    const activeUserId = user?._id || user?.userId || userId;
    const requestBody = {
      dlnumber: data.dlnumber,
      dob: data.dob,
      userId: activeUserId,
    };

    if (!requestBody.userId) {
      alert("❌ User ID is missing! Cannot proceed.");
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/kyc/verify/drivingLicence`, requestBody);
      alert("Driving License Verified Successfully!");
      if (refreshUser) await refreshUser();
      closeKYCModal();
    } catch (error) {
      console.error("❌ DL Verification Failed:", error?.response?.data?.message || error.message);
      alert("DL verification failed: " + (error?.response?.data?.message || error.message));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 font-sans pb-16">
      {/* Top Professional Header Bar */}
      <header className="bg-gradient-to-r from-[#0E3B2E] via-[#14532d] to-[#064e3b] text-white shadow-lg px-6 lg:px-12 py-5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-center md:text-left">
            <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 shadow-inner">
              <Leaf className="text-emerald-400 w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white flex items-center gap-2">
                User Dashboard
              </h1>
              <p className="text-xs md:text-sm text-emerald-200/80 font-medium">
                Pure Prakruti Green Fleet & Carbon Footprint Management
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 flex-wrap justify-center">
            {/* User Greeting Pill */}
            <div className="flex items-center gap-2.5 bg-white/10 backdrop-blur-md border border-white/15 px-4 py-2 rounded-full text-sm font-medium">
              <UserIcon className="w-4 h-4 text-emerald-300" />
              <span>Welcome, <strong className="text-emerald-200">{userName}</strong></span>
            </div>

            {/* KYC Status Badge */}
            {isKycVerified ? (
              <span className="inline-flex items-center gap-1.5 bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 px-4 py-2 rounded-full text-xs font-semibold shadow-sm">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                KYC Verified
              </span>
            ) : (
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5 bg-amber-500/20 border border-amber-400/40 text-amber-300 px-3.5 py-1.5 rounded-full text-xs font-semibold">
                  <ShieldAlert className="w-4 h-4 text-amber-400" />
                  KYC Pending
                </span>
                <button
                  onClick={openKYCModal}
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm px-5 py-2 rounded-full shadow-md transition transform hover:scale-105"
                >
                  Verify KYC
                </button>
              </div>
            )}

            {/* Logout Button */}
            <button
              onClick={logout}
              className="flex items-center gap-2 bg-red-600/80 hover:bg-red-600 text-white font-medium text-xs md:text-sm px-4 py-2 rounded-full shadow-md transition transform hover:scale-105 border border-red-500/40 cursor-pointer"
              title="Logout from system"
            >
              <LogOut className="w-4 h-4 text-red-100" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        {!isKycVerified ? (
          /* LOCKED STATE VIEW */
          <motion.div
            className="bg-white rounded-3xl border border-gray-200 shadow-xl p-8 md:p-14 text-center my-8 max-w-4xl mx-auto relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Top decorative gradient bar */}
            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-amber-500 via-emerald-600 to-teal-500" />

            <div className="w-20 h-20 bg-amber-50 text-amber-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm border border-amber-200/60">
              <Lock className="w-10 h-10" />
            </div>

            <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
              User Dashboard Locked
            </h2>

            <p className="text-gray-600 mt-3 max-w-xl mx-auto text-base leading-relaxed">
              Your carbon emission statistics, vehicle fleet analytics, and report downloads are protected. Please complete your <strong>one-time KYC verification</strong> to unlock full dashboard features.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 opacity-75">
                <Truck className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                <div className="text-xs font-semibold text-gray-500">Fleet Metrics</div>
                <div className="text-xs text-gray-400 mt-1">Locked 🔒</div>
              </div>
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 opacity-75">
                <BarChart2 className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                <div className="text-xs font-semibold text-gray-500">Emission Charts</div>
                <div className="text-xs text-gray-400 mt-1">Locked 🔒</div>
              </div>
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 opacity-75">
                <Download className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                <div className="text-xs font-semibold text-gray-500">PDF & CSV Reports</div>
                <div className="text-xs text-gray-400 mt-1">Locked 🔒</div>
              </div>
            </div>

            <div className="mt-10">
              <button
                onClick={openKYCModal}
                className="bg-[#0E3B2E] hover:bg-emerald-800 text-white font-bold text-lg px-9 py-4 rounded-2xl shadow-xl transition transform hover:scale-105 flex items-center gap-3 mx-auto"
              >
                <ShieldCheck className="w-6 h-6 text-emerald-400" />
                Verify KYC Now (Aadhar / PAN / DL)
              </button>
              <p className="text-xs text-gray-400 mt-3 font-medium">
                Takes less than 1 minute. Complete verification from any 1 of the 3 available options.
              </p>
            </div>
          </motion.div>
        ) : (
          /* UNLOCKED DASHBOARD CONTENT */
          <>
            {/* Stat Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Card 1: Top Emitting Vehicle */}
              <motion.div
                className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-gray-500">Top Emitting Vehicle</span>
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                    <Truck size={24} />
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-extrabold text-gray-900 leading-tight">
                    {topEmittingVehicle?.vehicleNumber ?? 'N/A'}
                  </div>
                  <div className="text-xs text-emerald-600 font-medium mt-1 flex items-center gap-1">
                    <span>Emission: {topEmittingVehicle ? `${topEmittingVehicle.carbonFootprint} kg` : '0 kg'}</span>
                  </div>
                </div>
              </motion.div>

              {/* Card 2: Total Vehicles Registered */}
              <motion.div
                className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-gray-500">Registered Vehicles</span>
                  <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <Car size={24} />
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-extrabold text-gray-900">
                    {totalVehicles}
                  </div>
                  <div className="text-xs text-gray-500 font-medium mt-1">
                    Active Fleet Registered
                  </div>
                </div>
              </motion.div>

              {/* Card 3: Total Carbon Emission */}
              <motion.div
                className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-gray-500">Total Carbon Emission</span>
                  <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                    <Leaf size={24} />
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-extrabold text-gray-900">
                    {totalEmission.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-lg font-semibold text-gray-500">kg</span>
                  </div>
                  <div className="text-xs text-emerald-600 font-medium mt-1">
                    🌱 Total Carbon Footprint Calculated
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Emission Over Time */}
              <motion.div
                className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm flex flex-col justify-between"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Emission Over Time</h3>
                    <p className="text-xs text-gray-500">Carbon emission trends by period</p>
                  </div>
                  <div className="flex bg-gray-100 p-1 rounded-xl gap-1">
                    {["All", "Week", "Month", "Year"].map((filter) => (
                      <button
                        key={filter}
                        onClick={() => setTimeFilter(filter)}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                          timeFilter === filter
                            ? "bg-white text-emerald-800 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        {filter}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="w-full h-[320px] flex items-center justify-center">
                  {filteredData.length > 0 ? (
                    <Chart
                      chartType="ColumnChart"
                      width="100%"
                      height="300px"
                      options={{
                        colors: ["#059669"],
                        legend: { position: "none" },
                        chartArea: { width: "85%", height: "75%" },
                        backgroundColor: "transparent",
                        hAxis: { textStyle: { color: "#6B7280", fontSize: 11 } },
                        vAxis: { textStyle: { color: "#6B7280", fontSize: 11 } },
                      }}
                      data={[
                        ["Date", "CO2 Emission (kg)"],
                        ...filteredData.map((item) => [item.date, item.carbonFootprint]),
                      ]}
                    />
                  ) : (
                    <p className="text-gray-400 text-sm">No emission data available for selected period.</p>
                  )}
                </div>
              </motion.div>

              {/* Route-wise Emissions */}
              <motion.div
                className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm flex flex-col justify-between"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900">Route-wise Emissions</h3>
                  <p className="text-xs text-gray-500">Distribution across major transit routes</p>
                </div>

                <div className="w-full h-[320px] flex items-center justify-center">
                  {routeWiseEmissionData.length > 0 ? (
                    <Chart chartType="PieChart" width="100%" height="300px" data={data} options={chartOptions} />
                  ) : (
                    <p className="text-gray-400 text-sm">No route-wise emission data available.</p>
                  )}
                </div>
              </motion.div>
            </div>

            {/* Sustainability Milestones & Social Media Sharing */}
            <MilestonesAndShare
              userName={userName}
              totalVehicles={totalVehicles}
              totalEmission={totalEmission}
              isKycVerified={isKycVerified}
              fleetList={dieselVehiclesData1}
            />

            {/* Fleet Overview Table */}
            <motion.div
              className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Fleet Emission Overview</h3>
                  <p className="text-xs text-gray-500">Detailed record of registered vehicles and emissions</p>
                </div>

                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search vehicle number / fuel..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition w-full sm:w-64"
                    />
                  </div>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="bg-[#0E3B2E] hover:bg-emerald-800 text-white font-semibold text-sm px-4 py-2 rounded-xl transition flex items-center gap-2 shrink-0 shadow-sm"
                  >
                    <Download className="w-4 h-4 text-emerald-400" />
                    Export Report
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-left text-sm text-gray-700">
                  <thead className="bg-[#0E3B2E] text-white font-semibold text-xs uppercase tracking-wider">
                    <tr>
                      <th className="py-3.5 px-4">Vehicle Number</th>
                      <th className="py-3.5 px-4">Fuel Type</th>
                      <th className="py-3.5 px-4">CO2 Emission (kg)</th>
                      <th className="py-3.5 px-4">Distance (km)</th>
                      <th className="py-3.5 px-4">Last Updated</th>
                      <th className="py-3.5 px-4 text-center">Certificate PDF</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-gray-400">
                          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-emerald-600" />
                          Loading fleet data...
                        </td>
                      </tr>
                    ) : filteredFleetList.length > 0 ? (
                      filteredFleetList.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50/80 transition-colors">
                          <td className="py-3.5 px-4 font-semibold text-gray-900">{item.vehicleNumber}</td>
                          <td className="py-3.5 px-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                              {item.fuelType}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 font-semibold text-emerald-700">{item.carbonFootprint} kg</td>
                          <td className="py-3.5 px-4 text-gray-600">{item.totalDistance} km</td>
                          <td className="py-3.5 px-4 text-gray-500 text-xs">{new Date(item.updatedAt).toLocaleDateString()}</td>
                          <td className="py-3.5 px-4 text-center">
                            <button
                              onClick={() => handleDownload(item.pdfUrl)}
                              className="p-1.5 text-emerald-700 hover:bg-emerald-50 rounded-lg transition"
                              title="Download PDF"
                            >
                              <FileText className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-gray-400 text-sm">
                          No fleet records found matching search.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </>
        )}
      </main>

      {/* KYC Verification Modal */}
      {showKYCModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-gradient-to-b from-[#0E3B2E] via-[#092b21] to-[#061e17] text-white w-full max-w-3xl p-6 sm:p-9 rounded-3xl shadow-2xl relative overflow-y-auto max-h-[90vh] border border-emerald-500/40">
            {/* Top Accent Line */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-600 rounded-t-3xl" />

            <button
              onClick={closeKYCModal}
              className="absolute top-5 right-5 text-gray-300 hover:text-white transition p-2 rounded-full hover:bg-white/10"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="flex items-center gap-3.5 mb-2 pt-1">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center text-emerald-400 shadow-inner">
                <ShieldCheck className="w-7 h-7" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-wide">KYC Verification</h2>
                <p className="text-xs text-emerald-300 font-medium">1-Time Verification Requirement</p>
              </div>
            </div>

            <div className="my-5 bg-emerald-950/70 border border-emerald-500/30 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-emerald-100">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span>Verification requires completion of <strong>ONLY 1</strong> of the 3 methods below.</span>
              </div>
              <span className="bg-emerald-500/20 text-emerald-300 font-bold px-3 py-1 rounded-full border border-emerald-400/30 whitespace-nowrap">
                Aadhar / PAN / DL
              </span>
            </div>

            <div className="space-y-4">
              <KYCSection
                title="Aadhar Verification"
                verified={Boolean(user?.kyc?.aadhar?.verified)}
                fields={[
                  { label: "Aadhar Number", key: "uid" },
                  { label: "Full Name", key: "name" },
                  { label: "Date of Birth", key: "dob", type: "date" },
                  { label: "Gender", key: "gender", type: "select", options: [
                    { label: "-- Select Gender --", value: "" },
                    { label: "Male (M)", value: "M" },
                    { label: "Female (F)", value: "F" },
                    { label: "Other", value: "O" }
                  ]},
                  { label: "Mobile Number", key: "mobile" }
                ]}
                onSubmit={handleAadharSubmit}
              />

              <KYCSection
                title="PAN Verification"
                verified={Boolean(user?.kyc?.pan?.verified)}
                fields={[
                  { key: "panno", label: "PAN Number" },
                  { key: "PANFullName", label: "Full Name (as per PAN)" },
                ]}
                user={user}
                onSubmit={handlePanSubmit}
              />

              <KYCSection
                title="Driving License Verification"
                verified={Boolean(user?.kyc?.drivingLicence?.verified)}
                fields={[
                  { key: "dlnumber", label: "DL Number" },
                  { key: "dob", label: "Date of Birth", type: "date" }
                ]}
                onSubmit={handleDlSubmit}
              />
            </div>
          </div>
        </div>
      )}

      {/* Download CSV Report Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-4">
          <div className="bg-[#0E3B2E] text-white p-6 sm:p-8 rounded-3xl shadow-2xl w-full max-w-xl relative border border-emerald-600/40">
            <button
              className="absolute top-4 right-4 text-gray-300 hover:text-white p-2 rounded-full hover:bg-white/10"
              onClick={() => setIsModalOpen(false)}
            >
              <X className="w-6 h-6" />
            </button>

            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Download className="w-5 h-5 text-emerald-400" />
              Download Carbon Footprint Report
            </h3>

            <div className="flex border-b border-emerald-800 pb-2 mb-4">
              <button
                className={`w-1/2 text-sm font-bold pb-2 transition ${activeTab === "fuel" ? "text-emerald-400 border-b-2 border-emerald-400" : "text-gray-400"}`}
                onClick={() => setActiveTab("fuel")}
              >
                Filter By Fuel Type
              </button>
              <button
                className={`w-1/2 text-sm font-bold pb-2 transition ${activeTab === "date" ? "text-emerald-400 border-b-2 border-emerald-400" : "text-gray-400"}`}
                onClick={() => setActiveTab("date")}
              >
                Filter By Date Range
              </button>
            </div>

            {activeTab === "fuel" && (
              <div className="space-y-2 mb-6">
                {["DIESEL", "PETROL", "CNG", "ETHANOL"].map((fuel) => (
                  <label key={fuel} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 cursor-pointer text-sm font-medium">
                    <input
                      type="radio"
                      name="fuelType"
                      value={fuel}
                      checked={selectedFuel === fuel}
                      onChange={() => setSelectedFuel(fuel)}
                      className="accent-emerald-400"
                    />
                    {fuel}
                  </label>
                ))}
              </div>
            )}

            {activeTab === "date" && (
              <div className="space-y-2 mb-6">
                {["Daily", "Weekly", "Monthly", "Yearly"].map((dateRange) => (
                  <label key={dateRange} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 cursor-pointer text-sm font-medium">
                    <input
                      type="radio"
                      name="dateRange"
                      value={dateRange}
                      checked={selectedDateRange === dateRange}
                      onChange={() => setSelectedDateRange(dateRange)}
                      className="accent-emerald-400"
                    />
                    {dateRange}
                  </label>
                ))}
              </div>
            )}

            <div className="text-right">
              <button
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 py-3 rounded-xl shadow-lg transition"
                onClick={() => {
                  downloadCSVReport();
                  setIsModalOpen(false);
                }}
              >
                Download CSV
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
